import { defineStore } from "pinia";
import { useAuthStore } from "./authStore";
import { useDsaReportsTicketsStore } from "./dsaReportsTicketsStore";
import { useDsaReportsActivitiesStore } from "./dsaReportsActivitiesStore";
import { useDsaReportsMilestonesStore } from "./dsaReportsMilestonesStore";
import { useDsaReportsProjectsStore } from "./dsaReportsProjectsStore"; // ✅ Import project store
import { useDsaReportsStore } from "./dsaReportsStore";
import { getDsaReportsPeriods } from "../features/dsa-reports/api/dsaReportsApi";

export const useDsaReportsPeriodsStore = defineStore("dsaReportsPeriodsStore", {
  state: () => ({
    totalBudgeted: 0,
    usedThisQuarter: 0,
    usedThisPeriod: 0,
    remainingHours: 0,
    maxHours: 10,
    budgetSource: "allowance",
    periodBudgetedHours: 0,
    allowanceBudgetedHours: null,
    periods: [],
    isLoading: false,
    error: null
  }),

  getters: {
    selectedBudgetedHours(state) {
      if (state.budgetSource === "allowance" && state.allowanceBudgetedHours !== null) {
        return state.allowanceBudgetedHours;
      }
      return state.periodBudgetedHours;
    },
    hasAllowanceBudget(state) {
      return state.allowanceBudgetedHours !== null;
    }
  },

  actions: {
    async fetchPeriods(contractId, startDate, endDate) {
      const authStore = useAuthStore();
      const ticketStore = useDsaReportsTicketsStore();
      const activityStore = useDsaReportsActivitiesStore();
      const milestoneStore = useDsaReportsMilestonesStore();
      const projectStore = useDsaReportsProjectsStore(); // ✅ Include project store

      if (!authStore.token) {
        throw new Error("Authentication token is not available.");
      }

      // ✅ Prevent unnecessary API calls
      if (this.periods.length > 0) {
        //console.log("Periods already fetched, skipping API call.");
        return;
      }

      try {
        this.isLoading = true;
        //console.log(`Fetching periods for contractId: ${contractId}`);

        // ✅ Fetch periods from API
        const response = await getDsaReportsPeriods({ contractId, startDate, endDate });

        this.periods = response.data;

        if (this.periods.length > 0) {
          //console.log("Retrieved Period IDs:", this.periods.map(p => p.id));
        } else {
          console.warn("No periods retrieved for the given contract.");
        }

        this.processPeriodData(contractId);

        // ✅ Fetch activities **only if they are not already loaded**
        if (activityStore.activities.length === 0) {
          await activityStore.fetchActivitiesFromPeriods();
        } else {
          //console.log("Skipping activity fetching. Data already exists.");
        }

        // ✅ Extract and fetch **tickets**
        const issueIds = activityStore.extractIssueIdsFromActivities();
        if (issueIds.length > 0 && ticketStore.tickets.length === 0) {
          //console.log("Extracted Issue IDs from Activities:", issueIds);
          await ticketStore.fetchTicketsFromActivities(issueIds.join(","));
        } else {
          //console.log("Skipping ticket fetching. Data already exists.");
        }

        // ✅ Extract and fetch **milestones**
        const milestoneIds = activityStore.extractMilestoneIdsFromActivities();
        if (milestoneIds.length > 0 && milestoneStore.milestones.length === 0) {
          //console.log("Extracted Milestone IDs from Activities:", milestoneIds);
          await milestoneStore.fetchMilestonesFromActivities(milestoneIds.join(","));
        } else {
          //console.log("Skipping milestone fetching. Data already exists.");
        }

        // ✅ Extract and fetch **projects (jobs)**
        const jobIds = activityStore.extractJobIdsFromActivities();
        if (jobIds.length > 0 && projectStore.projects.length === 0) {
          //console.log("Extracted Job IDs from Activities:", jobIds);
          await projectStore.fetchProjectsFromActivities();
        } else {
          //console.log("Skipping project fetching. Data already exists.");
        }

        // ✅ Update period usage calculations
        this.setPeriodHoursByTicketsProjectsAndMilestones();

        this.error = null;
      } catch (err) {
        console.error("Error fetching periods:", err);
        this.error = err.message;
        this.periods = [];
      } finally {
        this.isLoading = false;
      }
    },

    clearPeriods() {
      //console.log("Clearing period data.");
      this.periods = [];
      this.totalBudgeted = 0;
      this.usedThisQuarter = 0;
      this.usedThisPeriod = 0;
      this.remainingHours = 0;
      this.maxHours = 10;
      this.periodBudgetedHours = 0;
      this.allowanceBudgetedHours = null;
    },

    getAllowanceBudgetHours(contractId) {
      const myStore = useDsaReportsStore();
      const contract = myStore.contracts.find((item) => item.id === contractId);
      const allowanceTime = Number(contract?.periodTemplate?.allowanceTime);
      if (!Number.isFinite(allowanceTime)) {
        return null;
      }
      return allowanceTime / 3600;
    },

    applyBudgetSource() {
      if (this.budgetSource === "allowance" && this.allowanceBudgetedHours === null) {
        this.budgetSource = "periods";
      }
      const totalBudgetedHours = this.selectedBudgetedHours;
      this.setTotalBudgeted(totalBudgetedHours);
      this.setRemainingHours(totalBudgetedHours - this.usedThisQuarter);
      this.updateMaxHours();
    },

    updateMaxHours() {
      this.setMaxHours(
        Math.max(
          this.totalBudgeted,
          this.usedThisQuarter,
          this.usedThisPeriod
        )
      );
    },

    setBudgetSource(source) {
      this.budgetSource = source === "allowance" ? "allowance" : "periods";
      this.applyBudgetSource();
    },

    processPeriodData(contractId) {
      //console.log("Processing period data...");
      //this.periods.forEach(period => console.log("Period Object:", period));

      const totalSeconds = this.periods.reduce((acc, period) => acc + (period.budget?.value || 0), 0);
      const totalHours = totalSeconds / 3600;
      this.periodBudgetedHours = totalHours;
      this.allowanceBudgetedHours = this.getAllowanceBudgetHours(contractId);

      const totalUsageSeconds = this.periods.reduce((acc, period) => acc + (period.budget_Used?.value || 0), 0);
      const totalUsageHours = totalUsageSeconds / 3600;
      this.setUsedThisQuarter(totalUsageHours);

      this.applyBudgetSource();
    },

    setPeriodHoursByTicketsProjectsAndMilestones() {
      //console.log("Calculating period hours by tickets, projects, and milestones...");
    
      const ticketStore = useDsaReportsTicketsStore();
      const projectStore = useDsaReportsProjectsStore();
    
      // ✅ Sum up billable seconds from **tickets**
      const totalTicketSeconds = ticketStore.tickets.reduce((acc, ticket) => acc + (ticket.billable_Seconds || 0), 0);
      const totalTicketHours = totalTicketSeconds / 3600;
    

      // ✅ Sum up billable seconds from **projects (jobs)**
      const totalProjectSeconds = projectStore.projects.reduce((acc, project) => acc + (project.billable_Seconds || 0), 0);
      const totalProjectHours = totalProjectSeconds / 3600;
    
      const totalBillableHours = totalTicketHours + totalProjectHours;
    
      //console.log(`Total Billable Hours this period: ${totalBillableHours}`);
    
      this.setUsedThisPeriod(totalBillableHours);
      this.updateMaxHours();
    },

    extractIssueIdsFromActivities() {
      //console.log("Extracting Issue IDs from Activities...");
      const activityStore = useDsaReportsActivitiesStore();
      return activityStore.extractIssueIdsFromActivities();
    },

    setTotalBudgeted(hours) { this.totalBudgeted = hours; },
    setUsedThisQuarter(hours) { this.usedThisQuarter = hours; },
    setUsedThisPeriod(hours) { this.usedThisPeriod = hours; },
    setRemainingHours(hours) { this.remainingHours = hours; },
    setMaxHours(hours) { this.maxHours = Math.round(hours / 5) * 5; }
  }
});
